import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import { treatInvoice } from 'shared/serializer';
import type { CreditNote } from 'types/store/credit-note-state';
import { InvoiceResponseSchema } from './invoice-schemas';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useLegacyAxiosInstance,
} from './utils';

export async function loadCreditNoteFn(axiosInstance: Promise<AxiosInstance>, id: number) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/credit_notes/${id}`);

  const parseResult = InvoiceResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'credit-note');
    throw new SilentError();
  }

  return treatInvoice<CreditNote>(parseResult.data.data as unknown as CreditNote);
}

export function useLoadCreditNote(id: number | undefined, options: QueryOptions<CreditNote> = {}) {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<CreditNote, ApiError>({
    queryKey: ['credit-notes', id],
    queryFn: () => loadCreditNoteFn(axiosInstance, id!),
    enabled: isDefined(id),
    ...options,
  });

  return addResourceNameToQueryResult<CreditNote, ApiError, 'creditNote'>(
    'creditNote',
    queryResult,
  );
}
